import { get } from 'svelte/store'

import type { TSuiPaymentItem } from '@app/types'
import { getClientConfigByClientId } from '@lib/clients'
import { configStore } from '@src/stores/config'
import { getAssetPrice, getSellableConfigId, getSuiCredCoinType, getSuiObject } from '@src/utils/blockchain'
import { initSuiClient } from '@src/libs/sui'
import { SUI_CRED_DECIMALS } from '@src/constants/blockchain'
import { AssetType } from './payment.constants'
import type { TClientPaymentOption, TFiatCurrency, TPrice } from './payment.types'

type TCalculateTotalPriceResult = {
  totalSumCentsByCurrency: Partial<Record<TFiatCurrency, number>>
  totalSumCoins: number
  pricesByAsset: number[]
  feeToApply: number
}

export const getClientPaymentOptions = (): TClientPaymentOption[] => {
  const payment = getClientConfigByClientId(get(configStore).clientId).payment

  return Object.entries(payment).map(([provider, currency]) => ({
    provider,
    currency,
  })) as TClientPaymentOption[]
}

export const getEnabledFiatCurrencies = (): TFiatCurrency[] => {
  return getClientPaymentOptions().map(({ currency }) => currency)
}

export const getCurrencyTotal = (totals: Partial<Record<TFiatCurrency, number>>, currency: TFiatCurrency): number => {
  return totals[currency] ?? 0
}

export const isCredCoin = (packageId: string, type: string): boolean => {
  return `${packageId}::${type}::${type.toUpperCase()}` === getSuiCredCoinType()
}

const getAssetCollectionPrice = async (packageId: string, assetId: string): Promise<TPrice> => {
  const currencies = getEnabledFiatCurrencies()

  const entries = await Promise.all(
    currencies.map(async (currency) => {
      const [cents, coins] = await getAssetPrice(packageId, assetId, currency)

      return [currency, { cents, coins }] as const
    }),
  )

  return {
    fiat: Object.fromEntries(entries.map(([currency, price]) => [currency, price.cents])),
    coins: entries.find(([currency]) => currency === 'USD')?.[1].coins ?? 0,
  }
}

const getSellablePrice = async (
  packageId: string,
  options: {
    includeCoins?: boolean
    currencies?: TFiatCurrency[]
  } = {},
): Promise<TPrice> => {
  const { includeCoins = true, currencies = ['USD'] } = options

  const sellableConfigId = await getSellableConfigId(packageId)

  const { suiNetwork } = get(configStore)
  const client = await initSuiClient(packageId, suiNetwork)

  const fiatEntries = await Promise.all(
    currencies.map(async (currency) => {
      const cents = await client.Sellable.getPriceFiat(sellableConfigId, currency)

      return [currency, cents] as const
    }),
  )

  let coins = 0

  if (includeCoins) {
    const sellableObj = await getSuiObject(sellableConfigId)

    const {
      content: {
        fields: { price_coin },
      },
    } = sellableObj

    coins = +price_coin
  }

  return {
    fiat: Object.fromEntries(fiatEntries),
    coins,
  }
}

const getPrice = async (packageId: string, type: string, assetId?: string): Promise<TPrice> => {
  const isAssetCollection = !!assetId && type === AssetType.CREDENZA_ASSET_COLLECTION

  if (isAssetCollection) {
    return getAssetCollectionPrice(packageId, assetId)
  }

  const isCred = isCredCoin(packageId, type)

  return getSellablePrice(packageId, {
    includeCoins: !isCred,
    currencies: isCred ? getEnabledFiatCurrencies() : ['USD'],
  })
}

const addFiatTotals = (
  totals: Partial<Record<TFiatCurrency, number>>,
  price: TPrice,
  multiplier: number,
): Partial<Record<TFiatCurrency, number>> => {
  const nextTotals = { ...totals }

  for (const [currency, cents] of Object.entries(price.fiat)) {
    const fiatCurrency = currency as TFiatCurrency

    nextTotals[fiatCurrency] = (nextTotals[fiatCurrency] ?? 0) + cents * multiplier
  }

  return nextTotals
}

const applyFeeToFiatTotals = (
  totals: Partial<Record<TFiatCurrency, number>>,
  clientCredFee?: number,
): {
  totals: Partial<Record<TFiatCurrency, number>>
  feeToApply: number
} => {
  if (!clientCredFee) {
    return {
      totals,
      feeToApply: 0,
    }
  }

  const nextTotals = { ...totals }

  for (const currency of Object.keys(nextTotals) as TFiatCurrency[]) {
    const currentTotal = nextTotals[currency] ?? 0

    nextTotals[currency] = currentTotal + currentTotal * clientCredFee
  }

  return {
    totals: nextTotals,
    feeToApply: clientCredFee,
  }
}

export const calculateTotalPrice = async (
  assets: TSuiPaymentItem[],
  clientCredFee?: number,
): Promise<TCalculateTotalPriceResult> => {
  let totalSumCentsByCurrency: Partial<Record<TFiatCurrency, number>> = {}
  let totalSumCoins = 0
  const pricesByAsset: number[] = []

  for (const asset of assets) {
    const { packageId, type, amount, assetId } = asset

    const multiplier = amount || 1
    const price = await getPrice(packageId, type, assetId)

    totalSumCentsByCurrency = addFiatTotals(totalSumCentsByCurrency, price, multiplier)

    if (!isCredCoin(packageId, type)) {
      const coins = (price.coins * multiplier) / SUI_CRED_DECIMALS

      totalSumCoins += coins
      pricesByAsset.push(coins)
    }
  }

  const isCredOnlyPurchase = assets.length > 0 && assets.every(({ packageId, type }) => isCredCoin(packageId, type))

  const feeResult = isCredOnlyPurchase
    ? applyFeeToFiatTotals(totalSumCentsByCurrency, clientCredFee)
    : {
        totals: totalSumCentsByCurrency,
        feeToApply: 0,
      }

  return {
    totalSumCentsByCurrency: feeResult.totals,
    totalSumCoins,
    pricesByAsset,
    feeToApply: feeResult.feeToApply,
  }
}
